/**
 * useJobManagement Hooks
 *
 * React Query hooks for job categories, task types, and category-task
 * assignments used by the settings wizard.
 */

import { queryKeys } from '@/lib/query-client';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import type {
  AssignTaskBody,
  CreateJobCategoryBody,
  CreateTaskTypeBody,
  ReorderTasksBody,
  SetTaskFormsBody,
  UpdateJobCategoryBody,
  UpdateTaskTypeBody,
} from '@skewb-works/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/jobManagementApi';

// ---------------------------------------------------------------------------
// Job Categories
// ---------------------------------------------------------------------------

export function useJobCategories() {
  return useQuery({
    queryKey: queryKeys.settings.jobCategories(),
    queryFn: () => api.getJobCategories(),
    staleTime: 60 * 1000,
  });
}

export function useCreateJobCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateJobCategoryBody) => api.createJobCategory(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.jobCategories() });
    },
    meta: {
      resourceName: 'Job Category',
      action: 'create',
    },
  });
}

export function useUpdateJobCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateJobCategoryBody }) =>
      api.updateJobCategory(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.jobCategories() });
    },
    meta: {
      resourceName: 'Job Category',
      action: 'update',
    },
  });
}

export function useDeleteJobCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteJobCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.jobCategories() });
      showSuccessToast('Category Deleted', 'The job category has been removed.');
    },
    onError: (err: any) => {
      showErrorToast('Failed to Delete', err?.message || 'An unexpected error occurred.');
    },
    meta: {
      resourceName: 'Job Category',
      action: 'delete',
    },
  });
}

// ---------------------------------------------------------------------------
// Task Types
// ---------------------------------------------------------------------------

export function useTaskTypes() {
  return useQuery({
    queryKey: queryKeys.settings.taskTypes(),
    queryFn: () => api.getTaskTypes(),
    staleTime: 60 * 1000,
  });
}

export function useCreateTaskType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTaskTypeBody) => api.createTaskType(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.taskTypes() });
    },
    meta: {
      resourceName: 'Task Type',
      action: 'create',
    },
  });
}

export function useUpdateTaskType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateTaskTypeBody }) =>
      api.updateTaskType(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.taskTypes() });
    },
    meta: {
      resourceName: 'Task Type',
      action: 'update',
    },
  });
}

export function useDeleteTaskType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteTaskType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.taskTypes() });
    },
    meta: {
      resourceName: 'Task Type',
      action: 'delete',
    },
  });
}

// ---------------------------------------------------------------------------
// Category Tasks
// ---------------------------------------------------------------------------

export function useCategoryTasks(categoryId: number | null) {
  return useQuery({
    queryKey: queryKeys.settings.categoryTasks(categoryId ?? 0),
    queryFn: () => api.getCategoryTasks(categoryId!),
    enabled: categoryId !== null && categoryId > 0,
    staleTime: 30 * 1000,
  });
}

export function useAssignTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignTaskBody) => api.assignTask(body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.settings.categoryTasks(variables.jobCategoryId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.jobCategories() });
    },
    meta: {
      successTitle: 'Task Assigned',
      successMessage: 'Task assigned to category successfully',
    },
  });
}

export function useRemoveTaskAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, categoryId }: { id: number; categoryId: number }) =>
      api.removeTaskAssignment(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.settings.categoryTasks(variables.categoryId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.jobCategories() });
    },
    meta: {
      successTitle: 'Task Removed',
      successMessage: 'Task assignment removed successfully',
    },
  });
}

export function useReorderTasks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReorderTasksBody) => api.reorderTasks(body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.settings.categoryTasks(variables.categoryId),
      });
    },
    meta: {
      successTitle: 'Tasks Reordered',
      successMessage: 'Task order updated successfully',
    },
  });
}

// ---------------------------------------------------------------------------
// Task Forms
// ---------------------------------------------------------------------------

export function useTaskForms(taskTypeId: number | null) {
  return useQuery({
    queryKey: [...queryKeys.settings.taskTypes(), 'forms', taskTypeId],
    queryFn: () => api.getTaskForms(taskTypeId!),
    enabled: taskTypeId !== null,
    staleTime: 60 * 1000,
  });
}

export function useSetTaskForms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SetTaskFormsBody) => api.setTaskForms(body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.settings.taskTypes(), 'forms', variables.taskTypeId],
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.taskTypes() });
    },
    meta: {
      successTitle: 'Forms Updated',
      successMessage: 'Task forms configuration updated successfully',
    },
  });
}
